"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import {
  Users,
  LineChart,
  GraduationCap,
  ArrowRight,
} from "lucide-react";

interface AngebotCardProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  buttonText: string;
  buttonLink: string;
  delay: number;
  isVisible: boolean;
  comingSoon?: boolean;
}

function AngebotCard({
  icon,
  title,
  description,
  buttonText,
  buttonLink,
  delay,
  isVisible,
  comingSoon = false,
}: AngebotCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const relX = (e.clientX - centerX) / (rect.width / 2);
    const relY = (e.clientY - centerY) / (rect.height / 2);

    const maxTilt = 4;
    setTilt({
      rotateX: -relY * maxTilt,
      rotateY: relX * maxTilt,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setTilt({ rotateX: 0, rotateY: 0 });
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s`,
        perspective: "1000px",
      }}
    >
      <div
        ref={cardRef}
        className={`landing-card-glow angebot-card h-full`}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) ${isHovered ? 'translateZ(10px)' : ''}`,
          transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.4s ease-out',
          boxShadow: isHovered
            ? `${tilt.rotateY * 2}px ${-tilt.rotateX * 2 + 15}px 30px rgba(0, 0, 0, 0.3)`
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="h-full flex flex-col" style={{ padding: '1.5rem' }}>
          {/* Icon + Title nebeneinander */}
          <div className="flex items-center gap-4 mb-4 z-10">
            <div
              className="flex items-center justify-center rounded-full shrink-0"
              style={{
                width: "3rem",
                height: "3rem",
                backgroundColor: "#191919",
                border: "1px solid hsla(37, 18%, 66%, 0.3)",
              }}
            >
              <div style={{ color: "#b7ab98" }}>{icon}</div>
            </div>

            <h3
              style={{
                fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: "clamp(1.1rem, 2.5vw, 1.45rem)",
                fontWeight: 700,
                color: "#b7ab98",
                lineHeight: "130%",
                margin: 0,
              }}
            >
              {title}
            </h3>
          </div>

          {/* Description */}
          <p
            className="z-10 grow"
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: "clamp(0.9rem, 1.5vw, 1.1rem)",
              lineHeight: "170%",
              color: "hsla(37, 18%, 66%, 0.7)",
            }}
          >
            {description}
          </p>

          {/* CTA Button */}
          <div className="mt-8 z-10">
            {comingSoon ? (
              <button className="h-11 px-6 rounded-lg font-semibold text-sm bg-gray-800 text-gray-500 opacity-50 cursor-not-allowed w-full inline-flex items-center justify-center gap-2">
                {buttonText}
              </button>
            ) : (
              <a
                href={buttonLink}
                className="h-11 px-6 rounded-lg font-semibold text-sm bg-brand text-gray-950 hover:bg-brand-hover shadow-lg transition-colors w-full inline-flex items-center justify-center gap-2"
              >
                {buttonText}
                <ArrowRight
                  size={18}
                  style={{ transition: "transform 0.3s ease" }}
                />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AngebotSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(false);
  const rafRef = useRef<number | undefined>(undefined);

  // Mouse tracking for glow effect
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const cards = document.querySelectorAll(".angebot-card");

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        (card as HTMLElement).style.setProperty("--xPos", `${x}px`);
        (card as HTMLElement).style.setProperty("--yPos", `${y}px`);
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const checkVisible = useCallback(() => {
    if (!sectionRef.current || showContent) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (rect.top < windowHeight * 0.7) {
      setShowContent(true);
    }
  }, [showContent]);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(checkVisible);
    };

    checkVisible();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [checkVisible]);

  const angebote = [
    {
      icon: <Users size={24} strokeWidth={1.5} />,
      title: "1:1 Sessions",
      description: (
        <>
          Du weißt nicht, wo du anfangen sollst? Oder du hast spezifische Fragen, die Google nicht beantworten kann? In einer persönlichen Session klären wir genau das, was für{" "}
          <span style={{ color: "#f7931a" }}>deine</span> Situation relevant ist. Kein Verkaufsgespräch - echte Hilfe.
        </>
      ),
      buttonText: "Bald verfügbar",
      buttonLink: "/termine",
      comingSoon: true,
    },
    {
      icon: <LineChart size={24} strokeWidth={1.5} />,
      title: "Jeden Sonntag. 16:21 Uhr.",
      description: (
        <>
          Wo steht Bitcoin? Wohin könnte die Reise gehen? Jede Woche analysiere ich den Preis nach dem Elliott-Wellen-Prinzip und bespreche konkrete Strategien. Keine Glaskugel - aber fundierte Einschätzungen für{" "}
          <span style={{ color: "#f7931a" }}>deine</span> eigenen Entscheidungen.
        </>
      ),
      buttonText: "Zum Bitcoin Blog",
      buttonLink: "/blog",
      comingSoon: false,
    },
    {
      icon: <GraduationCap size={24} strokeWidth={1.5} />,
      title: "Lerne in deinem Tempo.",
      description: (
        <>
          Du willst Bitcoin von Grund auf verstehen? Kurse zu Selbstverwahrung, Sicherheit, Strategie - alles, was{" "}
          <span style={{ color: "#f7931a" }}>du</span> brauchst, um souverän mit Bitcoin umzugehen.
        </>
      ),
      buttonText: "Bald verfügbar",
      buttonLink: "/academy",
      comingSoon: true,
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh", backgroundColor: "#0d0d0d" }}
    >
      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-48">
        {/* Main Headline */}
        <h2
          className="text-center select-none mb-4"
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: "110%",
            color: "#b7ab98",
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s",
          }}
        >
          Drei Wege, wie wir{" "}
          <span style={{ color: "#f7931a" }}>dir</span> helfen.
        </h2>

        {/* Subheadline */}
        <p
          className="text-center select-none max-w-2xl mx-auto"
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 400,
            fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
            lineHeight: "160%",
            color: "hsla(37, 18%, 66%, 0.7)",
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1s ease-out 0.4s, transform 1s ease-out 0.4s",
            marginBottom: "5rem",
          }}
        >
          Wähle den Weg, der zu{" "}
          <span style={{ color: "#f7931a" }}>dir</span> passt.
        </p>

        {/* Three Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {angebote.map((angebot, idx) => (
            <AngebotCard
              key={angebot.title}
              icon={angebot.icon}
              title={angebot.title}
              description={angebot.description}
              buttonText={angebot.buttonText}
              buttonLink={angebot.buttonLink}
              comingSoon={angebot.comingSoon}
              delay={0.5 + idx * 0.15}
              isVisible={showContent}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
